/**
 * Enregistreur d'écran de la démo — 100 % navigateur, aucune lib externe.
 * getDisplayMedia (capture d'écran/onglet, geste utilisateur requis) + MediaRecorder (webm).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useT, tr } from '@/i18n';

export const RECORDER_UNSUPPORTED_MSG =
  tr('shell.features.demoDemoRecorder.yourBrowserDoesNotSupportScreen');

export function isRecordingSupported(): boolean {
  return (
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices &&
    typeof navigator.mediaDevices.getDisplayMedia === "function" &&
    typeof MediaRecorder !== "undefined"
  );
}

function pickMimeType(): string | undefined {
  const candidates = [
    "video/webm;codecs=vp9",
    "video/webm;codecs=vp8",
    "video/webm",
  ];
  for (const c of candidates) {
    try {
      if (MediaRecorder.isTypeSupported(c)) return c;
    } catch {
      /* ignore */
    }
  }
  return undefined;
}

export function todayStamp(): string {
  return new Date().toISOString().slice(0, 10);
}

export function routeSlug(route: string): string {
  const s = route.replace(/^\/+|\/+$/g, "").replace(/[^a-zA-Z0-9]+/g, "-");
  return s || "accueil";
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

export interface ScreenRecorder {
  supported: boolean;
  recording: boolean;
  elapsed: number; // secondes
  error: string | null;
  clearError: () => void;
  /** Démarre la capture (déclenche la demande de permission). Retourne true si démarré. */
  start: (filename: string, onFinished?: () => void) => Promise<boolean>;
  /** Arrête, assemble le Blob et télécharge. */
  stop: () => void;
}

export function useScreenRecorder(): ScreenRecorder {
  const { t: tx } = useT();
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const recRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const nameRef = useRef("demo-karexia.webm");
  const finishedCb = useRef<(() => void) | undefined>(undefined);
  const tick = useRef<ReturnType<typeof setInterval> | null>(null);

  const cleanup = useCallback(() => {
    if (tick.current) clearInterval(tick.current);
    tick.current = null;
    streamRef.current?.getTracks().forEach((t) => {
      try {
        t.stop();
      } catch {
        /* ignore */
      }
    });
    streamRef.current = null;
    recRef.current = null;
    setRecording(false);
  }, []);

  const stop = useCallback(() => {
    const rec = recRef.current;
    if (rec && rec.state !== "inactive") {
      try {
        rec.stop(); // onstop finalise
        return;
      } catch {
        /* fallthrough */
      }
    }
    cleanup();
  }, [cleanup]);

  const start = useCallback(
    async (filename: string, onFinished?: () => void): Promise<boolean> => {
      setError(null);
      if (!isRecordingSupported()) {
        setError(RECORDER_UNSUPPORTED_MSG);
        return false;
      }
      if (recRef.current) return false;
      try {
        const stream = await navigator.mediaDevices.getDisplayMedia({
          video: { frameRate: 30 },
          audio: false,
        });
        const mimeType = pickMimeType();
        const rec = mimeType
          ? new MediaRecorder(stream, { mimeType })
          : new MediaRecorder(stream);
        chunksRef.current = [];
        nameRef.current = filename;
        finishedCb.current = onFinished;
        rec.ondataavailable = (e: BlobEvent) => {
          if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
        };
        rec.onerror = () => {
          setError(tx('shell.features.demoDemoRecorder.errorDuringRecordingTryAgain'));
          cleanup();
        };
        rec.onstop = () => {
          try {
            if (chunksRef.current.length) {
              const blob = new Blob(chunksRef.current, {
                type: rec.mimeType || "video/webm",
              });
              downloadBlob(blob, nameRef.current);
            } else {
              // Nothing was captured (stopped at once / the shared surface sent no
              // frame): say so instead of « Arrêter & télécharger » silently doing nothing.
              setError(tx('shell.features.demoDemoRecorder.couldNotGenerateTheVideo'));
            }
          } catch {
            setError(tx('shell.features.demoDemoRecorder.couldNotGenerateTheVideo'));
          } finally {
            chunksRef.current = [];
            cleanup();
            const cb = finishedCb.current;
            finishedCb.current = undefined;
            cb?.();
          }
        };
        // Si l'utilisateur clique « Arrêter le partage » du navigateur → finalise.
        stream.getVideoTracks().forEach((t) => {
          t.onended = () => stop();
        });
        streamRef.current = stream;
        recRef.current = rec;
        rec.start(1000);
        setElapsed(0);
        setRecording(true);
        const t0 = Date.now();
        tick.current = setInterval(
          () => setElapsed(Math.floor((Date.now() - t0) / 1000)),
          500,
        );
        return true;
      } catch {
        // Permission refusée / annulée / non supporté : jamais d'exception non gérée.
        setError(RECORDER_UNSUPPORTED_MSG);
        cleanup();
        return false;
      }
    },
    [cleanup, stop],
  );

  // Nettoyage au démontage : libère le flux sans télécharger.
  useEffect(
    () => () => {
      const rec = recRef.current;
      if (rec) {
        rec.onstop = null;
        try {
          if (rec.state !== "inactive") rec.stop();
        } catch {
          /* ignore */
        }
      }
      streamRef.current?.getTracks().forEach((t) => t.stop());
      if (tick.current) clearInterval(tick.current);
    },
    [],
  );

  return {
    supported: isRecordingSupported(),
    recording,
    elapsed,
    error,
    clearError: () => setError(null),
    start,
    stop,
  };
}

export function formatElapsed(s: number): string {
  const m = Math.floor(s / 60);
  return `${String(m).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}
